function parseWhitelist(extra = []) {
  // 允許同站台
  const origins = new Set([location.origin.toLowerCase()]);
  const domains = new Set(); // 精確主機
  const suffixes = new Set(); // 通配後綴（b.com 表示 *.b.com 也可）

  for (const raw of extra || []) {
    const s = String(raw || "").trim();
    if (!s) continue;

    if (/^https?:\/\//i.test(s)) {
      // 完整 URL / origin
      try {
        origins.add(new URL(s).origin.toLowerCase());
      } catch (e) {}
      continue;
    }

    // 主機或通配：*.b.com / .b.com / b.com
    let host = s.replace(/^\*\./, "").replace(/^\./, "").toLowerCase();
    if (!host) continue;
    // 精確允許
    domains.add(host);
    // 也允許所有子網域（.b.com）
    suffixes.add(host);
  }
  return { origins, domains, suffixes };
}

function isExternal(href, allow) {
  if (!href || href.startsWith("#")) return false;
  if (/^(mailto:|tel:|sms:|javascript:)/i.test(href)) return false;

  const u = new URL(href, location.href);
  const origin = u.origin.toLowerCase();
  const host = u.hostname.toLowerCase();

  // 1) 完整 origin 直接放行
  if (allow.origins.has(origin)) return false;

  // 2) 精確主機放行
  if (allow.domains.has(host)) return false;

  // 3) 通配後綴放行（host === b.com 或以 .b.com 結尾）
  for (const suf of allow.suffixes) {
    if (host === suf || host.endsWith("." + suf)) return false;
  }

  // 否則視為外部
  return true;
}

function isSafariLike() {
  const ua = navigator.userAgent;
  const vendor = navigator.vendor || "";
  const isApple = /Apple/i.test(vendor);
  const isCriOS = /CriOS/i.test(ua);
  const isFxiOS = /FxiOS/i.test(ua);
  const isEdgiOS = /EdgiOS/i.test(ua);
  const isOPiOS = /OPiOS/i.test(ua);
  return isApple && /Safari/i.test(ua) && !isCriOS && !isFxiOS && !isEdgiOS && !isOPiOS;
}

// 安全覆寫（不可寫就略過）
function tryPatch(obj, key, makeHandler) {
  try {
    const desc = Object.getOwnPropertyDescriptor(obj, key) || Object.getOwnPropertyDescriptor(Object.getPrototypeOf(obj), key);
    if (!desc || desc.writable === false || desc.configurable === false) return false;
    const original = obj[key].bind(obj);
    obj[key] = makeHandler(original);
    return true;
  } catch (_) {
    return false;
  }
}

export function initExternalLinkGuard({
  store,
  whitelist = [],
  preopen = "auto", // safariMode='preopen' 時才用
  safariMode = "preopen", // 'confirm' | 'preopen'
  confirmText = "您即將前往外部網站，該網站內容與隱私由其自行負責。是否繼續？",
} = {}) {
  const allowed = parseWhitelist(whitelist);
  const SAFARI = isSafariLike();
  const NEED_PREOPEN = safariMode === "preopen" && (preopen === true ? true : preopen === "auto" ? SAFARI : false);

  if (!("__EXT_GUARD_BYPASS__" in window)) window.__EXT_GUARD_BYPASS__ = false;

  // Safari 同步 confirm：不會先開空白視窗
  function confirmThenOpen(url) {
    const ok = window.confirm(`${confirmText}\n\n${url}`);
    if (!ok) return;
    window.__EXT_GUARD_BYPASS__ = true;
    try {
      window.open(url, "_blank");
    } finally {
      setTimeout(() => {
        window.__EXT_GUARD_BYPASS__ = false;
      }, 0);
    }
  }

  // 1) 攔 <a>（捕獲階段）
  const clickHandler = (e) => {
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    let el = e.target;
    while (el && el !== document.body) {
      if (el.tagName === "A") break;
      el = el.parentElement;
    }
    const a = el;
    if (!a || !a.href) return;
    if (a.hasAttribute("download") || a.getAttribute("data-no-guard") !== null) return;

    // 自訂頁面（迷你 CMS）的內容整段跳過提醒。
    //
    // 這個提醒是給「平台沒審過的連結」用的 —— 例如會員自己貼上來的網址。
    // 自訂頁的內容是後台編出來的，每一條連結都是自己放的，再跳一次
    // 「此連結非本服務所有」只會讓自家的活動頁看起來很可疑。
    //
    // 判斷放在這裡而不是渲染時逐一補 data-no-guard：短碼（x-cta、x-product
    // 之類）是掛載之後才長出 <a>，事後補的那批會漏掉。
    //
    // 單一連結若真的想要提醒，在那個 <a> 上加 data-guard 就會擋回來。
    if (a.closest(".cms-content") && a.getAttribute("data-guard") === null) return;

    const raw = a.getAttribute("href") || a.href;
    if (!isExternal(raw, allowed)) return;

    e.preventDefault();
    const url = new URL(raw, location.href).toString();

    const target = "_blank";

    if (SAFARI && safariMode === "confirm") {
      // Safari：同步 confirm，不會先開空白視窗
      confirmThenOpen(url, target);
      return;
    }

    // 其它瀏覽器或 preopen 策略
    if (NEED_PREOPEN) {
      const stub = window.open("", "_blank");
      if (stub) store.commit("externalLink/SET_PENDING_WIN", stub);
    }
    store.dispatch("externalLink/prompt", { url, target });
  };
  document.addEventListener("click", clickHandler, true);

  // 2) 攔 window.open
  const _open = window.open;
  window.open = function (url, target, features) {
    if (window.__EXT_GUARD_BYPASS__) return _open.apply(window, arguments);
    try {
      const str = typeof url === "string" ? url : String(url);
      if (isExternal(str, allowed)) {
        const abs = new URL(str, location.href).toString();

        const forcedTarget = "_blank";

        if (SAFARI && safariMode === "confirm") {
          confirmThenOpen(abs);
          return null;
        }
        if (NEED_PREOPEN) {
          const stub = _open.call(window, "", "_blank");
          if (stub) store.commit("externalLink/SET_PENDING_WIN", stub);
        }

        store.dispatch("externalLink/prompt", { url: abs, target: target || "_blank" });
        return null;
      }
    } catch (_) {}
    return _open.apply(window, arguments);
  };

  // 3) assign/replace（可覆寫才攔；Safari confirm 模式也同步詢問）
  tryPatch(
    window.location,
    "assign",
    (orig) =>
      function (url) {
        if (window.__EXT_GUARD_BYPASS__) return orig(url);
        if (!isExternal(url, allowed)) return orig(url);

        const abs = new URL(url, location.href).toString();
        if (SAFARI && safariMode === "confirm") {
          confirmThenOpen(abs);
          return;
        }
        store.dispatch("externalLink/prompt", { url: abs, target: "_blank" });
      }
  );
  tryPatch(
    window.location,
    "replace",
    (orig) =>
      function (url) {
        if (window.__EXT_GUARD_BYPASS__) return orig(url);
        if (!isExternal(url, allowed)) return orig(url);

        const abs = new URL(url, location.href).toString();
        if (SAFARI && safariMode === "confirm") {
          confirmThenOpen(abs);
          return;
        }

        store.dispatch("externalLink/prompt", { url: abs, target: "_blank" });
      }
  );

  // 4) helper
  window.$openExternal = function (url, target) {
    // 注意：如果是內部連結，這裡還是會用原本的 _open 行為，可以視需求決定是否也要強制
    if (!isExternal(url, allowed)) return _open.call(window, url, target || "_self");

    const abs = new URL(url, location.href).toString();
    const forcedTarget = "_blank";

    if (SAFARI && safariMode === "confirm") {
      confirmThenOpen(abs);
      return;
    }

    if (NEED_PREOPEN) {
      const stub = _open.call(window, "", "_blank");
      if (stub) store.commit("externalLink/SET_PENDING_WIN", stub);
    }
    store.dispatch("externalLink/prompt", { url: abs, target: forcedTarget });
  };

  // （可選）回傳清理函式
  return () => {
    document.removeEventListener("click", clickHandler, true);
    window.open = _open;
  };
}
